/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at Sun Jan 15 2023
 **/

import { css } from "@emotion/react";
import { Row } from "@/frameworks/designStuff/components";
import { HTMLAttributes } from "react";

interface NavigationBarProps {
	type?: "left" | "center" | "right" | "distribute";
	children?: React.ReactNode;
}

interface NavigationBarContentProps {
	type: "left" | "center" | "right";
	maxWidth?: UnitString | "initial" | "inherit";
}

export function NavigationBar({
	type = "distribute",
	children,
	...rest
}: NavigationBarProps & HTMLAttributes<HTMLDivElement>) {
	return (
		<Row.header horizonAlign={type} verticalAlign="center" {...rest}>
			{children}
		</Row.header>
	);
}

function NavigationBarContent({
	type = "left",
	maxWidth,
	children,
	...rest
}: NavigationBarContentProps & HTMLAttributes<HTMLLIElement>) {
	return (
		<Row.ul horizonAlign={type} verticalAlign="center" css={[contentStyle({ type, maxWidth })]} {...rest}>
			{children}
		</Row.ul>
	);
}

function NavigationBarItem({ children, ...rest }: HTMLAttributes<HTMLLIElement>) {
	return <Row.li {...rest}>{children}</Row.li>;
}

NavigationBar.Content = NavigationBarContent;
NavigationBar.Item = NavigationBarItem;

const contentStyle = ({ type, maxWidth }: NavigationBarContentProps) => {
	return css`
		${maxWidthStyle({ type, maxWidth })}
		height: inherit;
		box-sizing: border-box;
	`;
};

const maxWidthStyle = ({ type, maxWidth }: NavigationBarContentProps) => {
	switch (type) {
		case "left":
			return css`
				max-width: ${maxWidth || "initial"};
			`;
		case "center":
			return css`
				max-width: ${maxWidth || "360px"};
			`;
		case "right":
			return css`
				max-width: ${maxWidth || "initial"};
			`;
		default:
			return css`
				max-width: ${maxWidth || "initial"};
			`;
	}
};
